
import { computed, defineComponent, onBeforeUnmount, onMounted, reactive, watch } from 'vue'
import { useRoute } from 'vue-router'

import columnsIcon from '@/assets/icons/columns.svg'
import refreshIcon from '@/assets/icons/refresh_blue.svg'
import NewButton from '@/ui-kit/NewButton/index.vue'
import NewCheckbox from '@/common/new-components/NewCheckbox/index.vue'
import Loader from '@/common/components/UI/Loader/index.vue'

import WorkplaceFilters from '@/modules/TMSModule/components/WorkplaceFilters/index.vue'
import WorkplaceItems from '@/modules/TMSModule/components/WorkplaceItems/index.vue'

import reportsAPI from '@/new-services/reports.service'

import { ColumnItemT } from '@/core/types/tables.types'

import { useTablesStore } from '@/modules/TablesModule/store/useTablesStore'
import useNotifications from '@/common/composable/useNotifications'
import {
    GetWorkplaceItemsParams,
    InventoryInvoicesItem,
    WorkplaceItem,
} from '@/new-services/types/reports'
import { useGlobalStore } from '@/store/globalStore'

const COLUMNS = [
    {
        id: 1,
        name: 'FTL заказы/<br/>LTL направления',
        key: 'initialState',
        tooltip:
            'Отслеживание остатков: вес, кубатура, даты обработки, негабарит и приоритетные клиенты.',
    },
    {
        id: 2,
        name: 'Заявки логиста<br/>на проверке',
        key: 'onReviewState',
        tooltip: 'Заявки с превышением себестоимости, ждут подтверждения руководителей.',
    },
    {
        id: 3,
        name: 'Заявки логиста<br/>на подписании',
        key: 'atSigningState',
        tooltip: 'Заявки, прошедшие проверку и ожидающие подписания.',
    },
    {
        id: 4,
        name: 'Ожидают отгрузки',
        key: 'createdState',
        tooltip: 'Рейсы без ТТН, готовые к погрузке; просрочки выделены красным.',
    },
    {
        id: 5,
        name: 'В транзите',
        key: 'onTheWayState',
        tooltip: 'Рейсы в пути через промежуточные города; опоздания подсвечиваются.',
    },
    {
        id: 6,
        name: 'Опоздавшие рейсы',
        key: 'delayedState',
        tooltip: 'Рейсы с незакрытыми ТТН или задержками; требуется указать причину.',
    },
    {
        id: 7,
        name: 'РПП на проверке',
        key: 'pbsOnReviewState',
        tooltip: 'Завершенные рейсы, ожидающие проверки перед выплатой.',
    },
    {
        id: 8,
        name: 'РПП подтверждено',
        key: 'pbsAcceptedState',
        tooltip: 'Заявки на выплату, подтверждённые бухгалтерией.',
    },
]

export default defineComponent({
    components: {
        NewButton,
        NewCheckbox,
        WorkplaceFilters,
        WorkplaceItems,
        columnsIcon,
        refreshIcon,
        Loader,
    },
    setup() {
        const notifications = useNotifications()
        const { path } = useRoute()
        const tablesStore = useTablesStore()

        const store = useGlobalStore()
        const ITEMS_TIME = 5 * 60 * 1000
        let timer: any = null

        const state = reactive({
            is_loading: false,
            is_failed: false,
            is_open: false,
            is_top_btn: false,
            items_ltl: [] as WorkplaceItem[],
            items_ftl: [] as WorkplaceItem[],
            columns: [] as ColumnItemT[],
            //
            first_column: [] as InventoryInvoicesItem[],
        })

        const filters = reactive({
            type: [] as string[],
            logistIds: [] as number[],
            isDelayed: null as number | null,
            isProject: null as number | null,
        })

        const loadFirstColumn = async () => {
            try {
                const response = await reportsAPI.getInventoryInvoices()
                state.first_column = response.data.data
            } catch (error) {
                state.is_failed = true
                console.error(error)
                notifications.notifyError({ error, title: 'При получении данных произошла ошибка' })
            }
        }

        const loadItems = async () => {
            try {
                state.is_failed = false
                state.is_loading = true

                const params = { ...filters }
                loadFirstColumn()
                const response = await reportsAPI.getWorkplaceItems(params)

                state.items_ltl = { ...response.data.data.ltl }
                state.items_ftl = { ...response.data.data.ftl }
            } catch (error) {
                state.is_failed = true
                console.error(error)
                notifications.notifyError({ error, title: 'При получении данных произошла ошибка' })
            } finally {
                state.is_loading = false
            }
        }

        const onApplyFilters = (params: GetWorkplaceItemsParams) => {
            filters.type = params.type
            filters.logistIds = params.logistIds
            filters.isDelayed = params.isDelayed
            filters.isProject = params.isProject

            loadItems()
        }

        const onResetFilters = () => {
            filters.type = []
            filters.logistIds = []
            filters.isDelayed = null
            filters.isProject = null

            loadItems()
        }

        const isAllColumnsChecked = computed(() =>
            state.columns.every((column) => column.isChecked)
        )

        const getColumns = () => (state.columns = tablesStore.getColumns(path, COLUMNS))
        const setColumns = () => tablesStore.setColumns(path, state.columns)

        const toggle = () => {
            if (state.is_open) setColumns()
            state.is_open = !state.is_open
        }

        const onAllColumnsSelect = (value: boolean) => {
            if (value) {
                state.columns.forEach((column) => {
                    column.isChecked = true
                })
            } else {
                state.columns.forEach((column) => {
                    column.isChecked = false
                })
            }
        }

        const onSetColumns = () => {
            setColumns()
            toggle()
        }

        const getColumnName = (name: string) => {
            return name.split('<br/>').join(' ')
        }

        const filteredColumns = computed(() => state.columns.filter((c) => c.isChecked))

        const scrollToTop = () => {
            const itemsContainer = document.querySelector('.WorkplaceItems')
            if (itemsContainer) {
                itemsContainer.scrollTo({ top: 0, behavior: 'smooth' })
            }
        }

        const onScroll = () => {
            const itemsContainer = document.querySelector('.WorkplaceItems')
            state.is_top_btn = Boolean(itemsContainer && itemsContainer.scrollTop > 100)
        }

        onMounted(() => {
            getColumns()
            store.closeSidebar()

            timer = setTimeout(loadItems, ITEMS_TIME)
        })

        onBeforeUnmount(() => {
            if (timer) clearTimeout(timer)
        })

        return {
            state,
            filters,
            onApplyFilters,
            onResetFilters,
            onSetColumns,
            toggle,
            onAllColumnsSelect,
            isAllColumnsChecked,
            filteredColumns,
            loadItems,
            getColumnName,
            scrollToTop,
            onScroll,
        }
    },
})
